import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { PageLink } from '@/components/kit/page-link'
import { PlatformIcon } from '@/components/kit/platform-icon'

import {
    mainNav,
    mobileNav,
    siteConfig,
    START_HREF,
    START_LABEL,
    TAGLINE
} from '@/constants/site'

export const Wordmark: FC<{ className?: string, dotClassName?: string }> = ({ className }) => {
    return (
        <span
            role='img'
            aria-label='Updraft'
            className={cn('block h-[1em] aspect-[1814/502] bg-no-repeat mix-blend-multiply', className)}
            style={{ backgroundImage: "url('/images/brand/logo.png')", backgroundSize: '119.7% auto', backgroundPosition: '54.9% 46.4%' }}
        />
    )
}

const SwapLabel: FC<{ children: string }> = ({ children }) => {
    return (
        <span className='swap-label'>
            <span>{children}</span>
            <span aria-hidden='true'>{children}</span>
        </span>
    )
}

export const Navbar: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [open, setOpen] = useState(false)
    const lastY = useRef(0)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const firstLinkRef = useRef<HTMLAnchorElement>(null)

    useScrollFrame(() => {
        const y = window.scrollY
        const delta = y - lastY.current

        setScrolled(y > 24)

        if (Math.abs(delta) > 8) {
            const hero = document.getElementById('hero')
            const overHero = !!hero && hero.getBoundingClientRect().bottom > 80

            setHidden(delta > 0 && y > 200 && !overHero)
            lastY.current = y
        }

        if (y < 10) setHidden(false)
    })

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router])

    useEffect(() => {
        const root = document.documentElement

        root.style.overflow = open ? 'hidden' : ''

        if (open) firstLinkRef.current?.focus()

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && open) {
                setOpen(false)
                toggleRef.current?.focus()
            }
        }

        window.addEventListener('keydown', onKey)

        return () => {
            window.removeEventListener('keydown', onKey)
            root.style.overflow = ''
        }
    }, [open])

    const isActive = (href: string) => {
        if (href === '/') return router.pathname === '/'

        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    return (
        <>
            <header className={cn(
                'fixed inset-x-0 top-0 z-[85] flex justify-center px-3 pt-3 transition-[translate] duration-500 ease-expo focus-within:translate-y-0 md:pt-4',
                hidden && !open ? '-translate-y-[140%]' : 'translate-y-0'
            )}>
                <nav
                    aria-label='Main'
                    className={cn(
                        'flex w-full items-center justify-between gap-3 rounded-full border border-ink/[0.08] bg-paper/80 shadow-[0_12px_34px_-18px_rgb(30_30_30/0.35)] backdrop-blur-xl backdrop-saturate-150 transition-[max-width,padding,background-color] duration-500 ease-expo',
                        scrolled ? 'max-w-[1120px] py-1.5 pl-5 pr-1.5' : 'max-w-[1200px] py-2 pl-6 pr-2',
                        open && 'bg-paper'
                    )}
                >
                    <PageLink
                        href='/'
                        aria-label='Updraft, front page'
                        className='flex shrink-0 items-center self-center text-[1.55rem] text-ink'
                    >
                        <Wordmark />
                    </PageLink>

                    <ul className='hidden items-center lg:flex'>
                        {mainNav.map(item => {
                            const active = isActive(item.href)

                            return (
                                <li key={item.href}>
                                    <PageLink
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'relative flex rounded-full px-2.5 py-2 text-[13.5px] font-medium leading-5 transition-colors duration-300 xl:px-3 xl:text-sm',
                                            active ? 'bg-ink text-cream' : 'text-ink/75 hover:bg-sand hover:text-ink'
                                        )}
                                    >
                                        <SwapLabel>{item.label}</SwapLabel>
                                    </PageLink>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='flex items-center gap-1.5'>
                        <PageLink
                            href={START_HREF}
                            className='group hidden h-10 items-center gap-2 rounded-full bg-ember pl-4 pr-1.5 text-sm font-medium text-ink shadow-[0_10px_24px_-12px_rgb(255_92_0/0.9)] transition-colors duration-300 hover:bg-[#ff6a14] sm:inline-flex'
                        >
                            <SwapLabel>{START_LABEL}</SwapLabel>
                            <span className='flex size-7 items-center justify-center rounded-full bg-ink text-cream'>
                                <ArrowUpRight className='size-4 transition-transform duration-500 ease-expo group-hover:rotate-45' />
                            </span>
                        </PageLink>

                        <button
                            ref={toggleRef}
                            type='button'
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            onClick={() => setOpen(value => !value)}
                            className='relative flex size-10 items-center justify-center rounded-full bg-ink text-cream lg:hidden'
                        >
                            <span className={cn('absolute h-[1.5px] w-4 bg-current transition-transform duration-500 ease-expo', open ? 'rotate-45' : '-translate-y-[3.5px]')} />
                            <span className={cn('absolute h-[1.5px] w-4 bg-current transition-transform duration-500 ease-expo', open ? '-rotate-45' : 'translate-y-[3.5px]')} />
                        </button>
                    </div>
                </nav>
            </header>

            <div
                id='mobile-menu'
                role='dialog'
                aria-modal='true'
                aria-label='Menu'
                inert={!open}
                className={cn(
                    'fixed inset-0 z-[84] flex flex-col overflow-y-auto bg-cream px-6 pb-8 pt-24 transition-[clip-path] duration-700 ease-snap lg:hidden',
                    open ? '[clip-path:inset(0_0_0_0)]' : 'pointer-events-none [clip-path:inset(0_0_100%_0)]'
                )}
            >
                <p className='rule-double pt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>
                    The Updraft Times, sections
                </p>

                <ul className='mt-2 flex flex-col'>
                    {mobileNav.map((item, index) => (
                        <li
                            key={item.href}
                            className='overflow-hidden border-b border-ink/10'
                        >
                            <PageLink
                                ref={index === 0 ? firstLinkRef : undefined}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className={cn(
                                    'flex items-baseline justify-between py-2 font-heading text-[clamp(2.6rem,13vw,4.5rem)] font-bold leading-[1] tracking-[-0.045em] transition-[translate,opacity] duration-700 ease-expo',
                                    open ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0',
                                    isActive(item.href) && 'text-ember'
                                )}
                                style={{ transitionDelay: open ? `${120 + index * 50}ms` : '0ms' } as CSSProperties}
                            >
                                {item.label}
                                <span className='font-mono text-xs font-normal tracking-normal text-stone'>
                                    p.{String(index + 1).padStart(2, '0')}
                                </span>
                            </PageLink>
                        </li>
                    ))}
                </ul>

                <div className={cn('mt-10 flex flex-col gap-6 transition-opacity delay-300 duration-700', open ? 'opacity-100' : 'opacity-0')}>
                    <PageLink
                        href={START_HREF}
                        onClick={() => setOpen(false)}
                        className='inline-flex h-14 items-center gap-3 self-start rounded-full bg-ember pl-6 pr-2 text-base font-medium text-ink'
                    >
                        {START_LABEL}
                        <span className='flex size-10 items-center justify-center rounded-full bg-ink text-cream'>
                            <ArrowUpRight className='size-4' />
                        </span>
                    </PageLink>

                    <div className='flex items-center gap-2'>
                        {siteConfig.socials.map(social => (
                            <a
                                key={social.platform}
                                href={social.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={`Updraft on ${social.label}`}
                                className='flex size-11 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors hover:border-ember hover:bg-ember'
                            >
                                <PlatformIcon
                                    name={social.platform}
                                    className='size-[18px]'
                                />
                            </a>
                        ))}
                    </div>

                    <p className='font-serif text-2xl italic text-ink'>{TAGLINE}</p>
                </div>
            </div>
        </>
    )
}
